import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ShieldCheck, GraduationCap, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Sidebar } from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

function getInitials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function Topbar({ role = "student", title }) {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Determine "switch view" target — only useful for admin user
  const isAdminUser = user?.role === "admin";
  const onAdminPage = location.pathname.startsWith("/admin");
  const switchTo = onAdminPage ? "/student/dashboard" : "/admin";
  const switchLabel = onAdminPage ? "Student Portal" : "Admin Dashboard";
  const SwitchIcon = onAdminPage ? GraduationCap : ShieldCheck;

  return (
    <header className="sticky top-0 z-40 glass-topbar" data-testid="topbar">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 min-w-0">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 max-w-[85vw]">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">Application menu</SheetDescription>
              <Sidebar role={role} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          {title && (
            <h1 className="text-base sm:text-xl font-heading font-semibold tracking-tight truncate" data-testid="page-title">
              {title}
            </h1>
          )}
          {role === "student" && (() => {
            const list = Array.isArray(user?.batches) && user.batches.length > 0
              ? user.batches
              : (user?.batch ? [user.batch] : []);
            if (list.length === 0) return null;
            return (
              <div className="hidden sm:flex items-center gap-1.5 ml-2" data-testid="topbar-batch-access">
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Access</span>
                <div className="flex items-center gap-1">
                  {list.map((b) => (
                    <span
                      key={b}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-primary/12 text-primary border border-primary/30"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="flex items-center gap-2">
          {/* Admin-only View Switcher */}
          {isAdminUser && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex"
              data-testid="view-switcher"
              title={`Switch to ${switchLabel}`}
            >
              <Link to={switchTo}>
                <SwitchIcon className="h-3.5 w-3.5" />
                <span>{switchLabel}</span>
              </Link>
            </Button>
          )}
          {isAdminUser && (
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="sm:hidden"
              data-testid="view-switcher-mobile"
              title={`Switch to ${switchLabel}`}
            >
              <Link to={switchTo}>
                <ArrowLeftRight className="h-4 w-4" />
              </Link>
            </Button>
          )}

          <ThemeToggle />
          {user && (
            <div className="flex items-center gap-3 pl-2 border-l border-border ml-1">
              <div className="hidden md:flex flex-col items-end leading-tight">
                <span className="text-sm font-medium" data-testid="topbar-user-name">{user.name || user.email}</span>
                <span className="text-xs text-muted-foreground capitalize">{user.role}</span>
              </div>
              <Avatar className="h-9 w-9 ring-2 ring-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                  {getInitials(user.name || user.email)}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
